import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { User, Menu, X, ChevronDown, BookMarked, LogOut } from "lucide-react";
import { T, R, EMBLEM } from "./tokens";
import { useLanguage, SUPPORTED_LANGUAGES } from "../../lib/useLanguage";
import { useAuth } from "../../context/AuthContext";

const LINKS = [
  { label: "Library",      to: R.library     },
  { label: "Collections",  to: R.collections },
  { label: "MyCraftz",     to: R.mycraftz    },
  { label: "My Sanctuary", to: R.sanctuary   },
];

/**
 * Shared top navigation.
 *
 * • Desktop (≥768px): unchanged from prior implementation — brand on the
 *   left, centred link row, bell + profile actions on the right.
 * • Mobile (<768px):  brand on the left, gold hamburger on the right.
 *   Tapping the hamburger drops a dark-brown drawer beneath the nav bar
 *   with the four LINKS + bell/profile actions. Adequate tap targets
 *   (≥48px), F_HEADING serif caps, gold hover/active accents.
 */
export default function Navigation({ activePath }) {
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useLanguage();
  const location = useLocation();

  // Track viewport across mobile breakpoint.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    // Support both modern and legacy Safari.
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  // Close drawer on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  // ESC closes drawer.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      <nav
        data-testid="homev3-nav"
        className="fixed top-0 left-0 right-0 z-50 flex items-center"
        style={{
          height: 56,
          background: "rgba(20,10,5,0.85)",
          backdropFilter: "blur(8px)",
          paddingLeft:  isMobile ? 20 : 40,
          paddingRight: isMobile ? 12 : 40,
        }}
      >
        <Link
          to={R.home}
          data-testid="homev3-nav-brand"
          className="no-underline flex-shrink-0 flex items-center"
          style={{ fontFamily: T.F_DISPLAY, fontSize: 20, fontWeight: 700, color: T.PARCHMENT_LT, letterSpacing: "0.04em", gap: 10 }}
        >
          {!isMobile && (
            <img
              src={EMBLEM}
              alt=""
              aria-hidden
              data-testid="homev3-nav-emblem"
              className="homev3-nav__emblem"
              style={{ height: 40, width: "auto", display: "block", pointerEvents: "none" }}
            />
          )}
          <span>
            <span style={{ fontFamily: T.F_BODY, fontSize: "0.72em", verticalAlign: "baseline", letterSpacing: 0, marginRight: "0.02em" }}>i</span>CRAFTZ
          </span>
        </Link>

        {isMobile ? (
          // ── Mobile: hamburger only ────────────────────────────────────
          <div className="flex-1 flex justify-end">
            <button
              type="button"
              data-testid="homev3-nav-hamburger"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="homev3-nav-drawer"
              onClick={() => setMenuOpen((v) => !v)}
              style={{
                width: 44,
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: `1px solid ${menuOpen ? T.GOLD : "rgba(201,168,76,0.4)"}`,
                borderRadius: 6,
                color: T.GOLD,
                cursor: "pointer",
                transition: "border-color 150ms ease",
              }}
            >
              {menuOpen ? <X size={20} strokeWidth={1.75} /> : <Menu size={20} strokeWidth={1.75} />}
            </button>
          </div>
        ) : (
          // ── Desktop: brand + pill (left) · links (centered) · profile (right) ──
          // Language pill lives on the LEFT next to the brand, deliberately
          // far away from the top-right decorative Saraswati book emblem so
          // there is no visual clash and the emblem stays intact.
          <>
            <div className="flex items-center flex-shrink-0" style={{ marginLeft: 20 }}>
              <LangSelect
                lang={lang}
                setLang={setLang}
                testIdPrefix="homev3-nav"
              />
            </div>

            <div className="flex-1 flex items-center justify-center gap-9">
              {LINKS.map(({ label, to }) => {
                const active = activePath === to;
                return (
                  <Link
                    key={label}
                    to={to}
                    data-testid={`homev3-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="no-underline transition-colors duration-150"
                    style={{
                      fontFamily: T.F_HEADING,
                      fontSize: 13,
                      color: active ? T.GOLD : T.PARCHMENT_LT,
                      letterSpacing: "0.06em",
                      borderBottom: active ? `1px solid ${T.GOLD}` : "1px solid transparent",
                      paddingBottom: 2,
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = T.GOLD; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = T.PARCHMENT_LT; }}
                  >
                    {label}
                  </Link>
                );
              })}
            </div>

            <div className="flex items-center gap-5 flex-shrink-0">
              <ProfileMenu />
            </div>
          </>
        )}
      </nav>

      {/* ── Mobile drawer ───────────────────────────────────────────── */}
      {isMobile && menuOpen && (
        <>
          {/* Backdrop — taps close */}
          <div
            data-testid="homev3-nav-drawer-backdrop"
            onClick={closeMenu}
            style={{
              position: "fixed",
              top: 56,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 49,
            }}
          />

          <div
            id="homev3-nav-drawer"
            data-testid="homev3-nav-drawer"
            role="menu"
            style={{
              position: "fixed",
              top: 56,
              left: 0,
              right: 0,
              zIndex: 50,
              background: "rgba(20,10,5,0.98)",
              borderTop: `1px solid ${T.GOLD}`,
              boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
            }}
          >
            {LINKS.map(({ label, to }) => {
              const active = activePath === to;
              return (
                <Link
                  key={label}
                  to={to}
                  role="menuitem"
                  data-testid={`homev3-nav-drawer-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={closeMenu}
                  className="no-underline block"
                  style={{
                    fontFamily: T.F_HEADING,
                    fontSize: 15,
                    color: active ? T.GOLD : T.PARCHMENT_LT,
                    letterSpacing: "0.10em",
                    textTransform: "uppercase",
                    padding: "16px 24px",
                    minHeight: 48,
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgba(201,168,76,0.12)",
                    borderLeft: active ? `3px solid ${T.GOLD}` : "3px solid transparent",
                  }}
                >
                  {label}
                </Link>
              );
            })}

            <div style={{ padding: "16px 24px 8px" }}>
              <LangSelect
                lang={lang}
                setLang={setLang}
                testIdPrefix="homev3-nav-drawer"
                fullWidth
              />
            </div>

            <div style={{ display: "flex", padding: "16px 24px" }}>
              <MobileProfileAction closeMenu={closeMenu} />
            </div>
          </div>
        </>
      )}
    </>
  );
}

/**
 * LangSelect — parchment-styled native <select> used in both the desktop
 * action row and the mobile drawer. Native <select> keeps a11y + platform
 * picker semantics; we style the visible pill and let the browser render
 * the option list (matches the v1 MasterHomepage pattern).
 */
function LangSelect({ lang, setLang, testIdPrefix, fullWidth = false }) {
  const activeGlyph = (SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[0]).glyph;
  return (
    <div
      data-testid={`${testIdPrefix}-lang-wrap`}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        width: fullWidth ? "100%" : "auto",
      }}
    >
      {/* Painted pill (decorative) */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(201,168,76,0.10)",
          border: `1px solid ${T.GOLD}`,
          borderRadius: 999,
          pointerEvents: "none",
        }}
      />
      {/* Visible label + chevron */}
      <div
        aria-hidden
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: fullWidth ? "10px 32px 10px 16px" : "6px 26px 6px 12px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: T.GOLD,
          minHeight: fullWidth ? 44 : 30,
          minWidth: fullWidth ? "100%" : 62,
          justifyContent: fullWidth ? "space-between" : "flex-start",
        }}
      >
        <span>{activeGlyph}</span>
        <ChevronDown size={14} strokeWidth={1.75} style={{ position: fullWidth ? "static" : "absolute", right: fullWidth ? undefined : 8 }} />
      </div>
      {/* Real native <select> — invisible, sits on top for interaction. */}
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        data-testid={`${testIdPrefix}-lang-select`}
        aria-label="Select language"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
          border: 0,
          background: "transparent",
          color: "transparent",
          appearance: "none",
        }}
      >
        {SUPPORTED_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code} style={{ color: "#000" }}>
            {l.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * ProfileMenu — desktop top-right profile control.
 * • Logged out (or auth still resolving): a plain gold profile icon that
 *   links straight to /login (unchanged legacy behaviour).
 * • Logged in: the icon becomes a dropdown trigger exposing "My Sanctuary"
 *   (/my-sanctuary) and "Sign out" (clears the session → homepage). This is
 *   the primary sign-out control in the app.
 */
function ProfileMenu() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!isAuthenticated) {
    return (
      <Link
        to={R.profile}
        data-testid="homev3-nav-profile"
        aria-label="Sign in"
        className="flex items-center"
        style={{ color: T.GOLD }}
      >
        <User size={18} strokeWidth={1.5} />
      </Link>
    );
  }

  const handleSignOut = () => {
    setOpen(false);
    logout();
    navigate("/");
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <button
        type="button"
        data-testid="homev3-nav-profile"
        aria-label="Profile menu"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center"
        style={{ background: "transparent", border: "none", cursor: "pointer", color: T.GOLD, padding: 0 }}
      >
        <User size={18} strokeWidth={1.5} />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="homev3-nav-profile-menu"
          style={{
            position: "absolute",
            top: "calc(100% + 12px)",
            right: 0,
            minWidth: 190,
            background: "rgba(20,10,5,0.98)",
            border: `1px solid ${T.GOLD}`,
            borderRadius: 8,
            boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
            overflow: "hidden",
            zIndex: 60,
          }}
        >
          <Link
            to={R.sanctuary}
            role="menuitem"
            data-testid="homev3-nav-profile-sanctuary"
            onClick={() => setOpen(false)}
            className="no-underline"
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "12px 16px", minHeight: 44,
              color: T.PARCHMENT_LT, fontFamily: T.F_HEADING, fontSize: 12,
              letterSpacing: "0.06em",
              borderBottom: "1px solid rgba(201,168,76,0.16)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.PARCHMENT_LT; }}
          >
            <BookMarked size={15} strokeWidth={1.6} /> My Sanctuary
          </Link>
          <button
            type="button"
            role="menuitem"
            data-testid="homev3-nav-profile-signout"
            onClick={handleSignOut}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 10,
              padding: "12px 16px", minHeight: 44,
              background: "transparent", border: "none", cursor: "pointer",
              color: T.PARCHMENT_LT, fontFamily: T.F_HEADING, fontSize: 12,
              letterSpacing: "0.06em", textAlign: "left",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = T.GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = T.PARCHMENT_LT; }}
          >
            <LogOut size={15} strokeWidth={1.6} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * MobileProfileAction — bottom action inside the mobile drawer.
 * Logged in → "Sign out"; logged out → "Sign in" (→ /login). "My Sanctuary"
 * is already present in the drawer's main link list.
 */
function MobileProfileAction({ closeMenu }) {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const baseStyle = {
    flex: 1,
    minHeight: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    border: `1px solid rgba(201,168,76,0.4)`,
    borderRadius: 6,
    color: T.GOLD,
    fontFamily: T.F_HEADING,
    fontSize: 12,
    letterSpacing: "0.08em",
    background: "transparent",
  };

  if (!isAuthenticated) {
    return (
      <Link
        to={R.profile}
        data-testid="homev3-nav-drawer-profile"
        aria-label="Sign in"
        onClick={closeMenu}
        className="no-underline"
        style={baseStyle}
      >
        <User size={16} strokeWidth={1.5} />
        <span>SIGN IN</span>
      </Link>
    );
  }

  return (
    <button
      type="button"
      data-testid="homev3-nav-drawer-signout"
      onClick={() => { closeMenu(); logout(); navigate("/"); }}
      style={{ ...baseStyle, cursor: "pointer" }}
    >
      <LogOut size={16} strokeWidth={1.5} />
      <span>SIGN OUT</span>
    </button>
  );
}


